import { useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  FileText,
  Palette,
  Pencil,
  PenLine,
  Plus,
  Share2,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { LinkCompartilhadoResponse, RequisicaoResponse, RequisicaoResponseStatus } from '@/api/generated/model';
import {
  getRequisicoesControllerListarQueryKey,
  getRequisicoesControllerPdfUrl,
  useRequisicoesControllerAssinar,
  useRequisicoesControllerCompartilhar,
  useRequisicoesControllerCompartilharPorEmail,
  useRequisicoesControllerExcluir,
  useRequisicoesControllerListar,
} from '@/api/generated/requisicoes-exames/requisicoes-exames';
import { ApiError, urlDeArquivo } from '@/api/http-client';
import { CompartilharDialogo } from '@/shared/components/CompartilharDialogo';
import { LayoutDocumentoDialogo } from '@/features/documentos/components/LayoutDocumentoDialogo';
import { SeloDeAssinaturaDigital } from '@/features/documentos/components/SeloDeAssinaturaDigital';
import { useSenhaDoCertificado } from '@/features/documentos/lib/useSenhaDoCertificado';
import { RequisicaoFormDialogo } from '@/features/requisicoes/components/RequisicaoFormDialogo';
import { CLASSE_STATUS, ROTULO_STATUS, STATUS } from '@/features/requisicoes/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { toast } from '@/shared/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/**
 * Requisições de exames (F3-6).
 *
 * ⚠️ **Assinar é o ponto sem volta.** Depois de assinada a requisição vira
 * imutável e o PDF passa a usar o snapshot congelado — o rascunho gera prévia
 * a partir do cadastro atual. Compartilhar só funciona depois de assinar: o
 * servidor responde 409, e a tela diz isso em vez de repetir a mensagem crua.
 */
export function RequisicoesPage() {
  const { pode, carregando: carregandoPermissoes } = usePermissoes();
  const queryClient = useQueryClient();
  const [parametros, setParametros] = useSearchParams();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<string>(TODOS);
  const [formAberto, setFormAberto] = useState(false);
  const [layoutAberto, setLayoutAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<RequisicaoResponse | undefined>();
  const [aAssinar, setAAssinar] = useState<RequisicaoResponse | null>(null);
  const [aExcluir, setAExcluir] = useState<RequisicaoResponse | null>(null);
  /**
   * O documento vai junto do link: o diálogo precisa do paciente e do
   * veterinário para montar o texto do WhatsApp e do e-mail, e a listagem é a
   * única que os tem à mão.
   */
  const [compartilhando, setCompartilhando] = useState<{
    documento: RequisicaoResponse;
    link: LinkCompartilhadoResponse;
  } | null>(null);

  const { data, isPending, isError, refetch } = useRequisicoesControllerListar({
    pagina,
    porPagina: 20,
    ordenarPor: 'criadoEm',
    ordem: 'desc',
    ...(busca.trim() ? { busca: busca.trim() } : {}),
    ...(status !== TODOS ? { status: status as RequisicaoResponseStatus } : {}),
  });

  const assinar = useRequisicoesControllerAssinar();
  const { comSenhaSeNecessario, dialogo: dialogoDeSenha } = useSenhaDoCertificado();
  const compartilhar = useRequisicoesControllerCompartilhar();
  const compartilharPorEmail = useRequisicoesControllerCompartilharPorEmail();
  const excluir = useRequisicoesControllerExcluir();

  const podeCriar = pode('requisicoes_exames.criar');
  const podeEditar = pode('requisicoes_exames.editar');
  const podeExcluir = pode('requisicoes_exames.excluir');
  const podeAssinar = pode('requisicoes_exames.assinar');

  /**
   * Deep link do atalho do Dashboard (`?novo=1`): abre o formulário de criação.
   *
   * O parâmetro é consumido na hora, com `replace`, para o F5 e o "voltar" não
   * reabrirem um diálogo que o usuário já tinha fechado.
   *
   * ⚠️ Espera as permissões: enquanto o `/me` não responde, `podeCriar` é
   * `false` por ser fail-closed, e agir nesse `false` gastaria o parâmetro sem
   * abrir nada — o atalho falharia justamente na primeira carga, que é quando
   * ele é usado.
   */
  useEffect(() => {
    if (carregandoPermissoes) return;
    if (parametros.get('novo') !== '1') return;

    const semParametro = new URLSearchParams(parametros);
    semParametro.delete('novo');
    setParametros(semParametro, { replace: true });

    if (!podeCriar) return;
    setEmEdicao(undefined);
    setFormAberto(true);
  }, [carregandoPermissoes, parametros, podeCriar, setParametros]);

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  async function confirmarAssinatura(requisicao: RequisicaoResponse) {
    try {
      const assinada = await comSenhaSeNecessario((senhaCertificado) =>
        assinar.mutateAsync({
          id: requisicao.id,
          ...(senhaCertificado ? { data: { senhaCertificado } } : {}),
        }),
      );
      if (!assinada) return;

      await queryClient.invalidateQueries({ queryKey: getRequisicoesControllerListarQueryKey() });
      toast.success(assinada.assinaturaDigital ? 'Requisição assinada digitalmente.' : 'Requisição assinada.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível assinar.');
    } finally {
      setAAssinar(null);
    }
  }

  async function gerarLink(requisicao: RequisicaoResponse) {
    try {
      // Corpo vazio de propósito: `validadeHoras` é opcional e o servidor usa
      // 72h. Pôr um seletor aqui acrescentaria uma decisão a uma ação de dois
      // cliques — e o dialog mostra a validade que voltou, que é o que importa.
      setCompartilhando({
        documento: requisicao,
        link: await compartilhar.mutateAsync({ id: requisicao.id, data: {} }),
      });
    } catch (erro) {
      // 409 é o caso previsto: documento ainda não assinado. A mensagem do
      // servidor é genérica; aqui dizemos o que fazer a respeito.
      if (erro instanceof ApiError && erro.status === 409) {
        toast.error('Assine a requisição antes de compartilhar.');
        return;
      }
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível gerar o link.');
    }
  }

  async function confirmarExclusao(requisicao: RequisicaoResponse) {
    try {
      await excluir.mutateAsync({ id: requisicao.id });
      await queryClient.invalidateQueries({ queryKey: getRequisicoesControllerListarQueryKey() });
      toast.success('Requisição excluída.');
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível excluir.');
    } finally {
      setAExcluir(null);
    }
  }

  function abrirPdf(requisicao: RequisicaoResponse) {
    // Não passa pelo cliente gerado de propósito — ver `urlDeArquivo`.
    window.open(urlDeArquivo(getRequisicoesControllerPdfUrl(requisicao.id)), '_blank', 'noopener');
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <Input
            value={busca}
            onChange={(e) => aoFiltrar(() => setBusca(e.target.value))}
            placeholder="Buscar por número ou paciente..."
            className="sm:max-w-xs"
          />

          <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
            <SelectTrigger className="sm:w-44" aria-label="Filtrar por status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODOS}>Todos os status</SelectItem>
              {STATUS.map((s) => (
                <SelectItem key={s} value={s}>
                  {ROTULO_STATUS[s] ?? s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* O wrapper existe porque agora são dois botões — antes o "Nova
            requisição" era filho direto do flex do cabeçalho. */}
        <div className="flex flex-wrap gap-2">
          {/* Fora do guard `podeCriar` de propósito: layout é configuração
              PESSOAL de quem está logado, não permissão sobre a requisição —
              o mesmo recorte de "Modelos" na página de receituários. */}
          <Button variant="outline" onClick={() => setLayoutAberto(true)}>
            <Palette className="w-4 h-4" />
            Configuração de layout
          </Button>

          {podeCriar && (
            <Button
              onClick={() => {
                setEmEdicao(undefined);
                setFormAberto(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Nova requisição
            </Button>
          )}
        </div>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Requisições de exames
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={ClipboardList}
            titulo="Nenhuma requisição encontrada"
            descricao={
              busca || status !== TODOS
                ? 'Tente outro termo ou limpe os filtros.'
                : 'Crie a primeira requisição para começar.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Paciente</TableHead>
                    <TableHead>Exames</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Veterinário</TableHead>
                    <TableHead className="w-40 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((requisicao) => (
                    <TableRow key={requisicao.id}>
                      <TableCell className="font-medium text-foreground whitespace-nowrap">
                        {/* Rascunho ainda não tem número: ele é atribuído na
                            assinatura, e mostrar "—" é mais honesto que fingir. */}
                        {requisicao.numero ?? '—'}
                      </TableCell>
                      <TableCell className="text-foreground">{requisicao.pacienteNome}</TableCell>
                      <TableCell>
                        <span className="text-foreground">{requisicao.itens.length}</span>
                        <p className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate">
                          {requisicao.itens.map((i) => i.nome).join(', ')}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={CLASSE_STATUS[requisicao.status]}>
                          {ROTULO_STATUS[requisicao.status] ?? requisicao.status}
                        </Badge>
                        {requisicao.assinaturaDigital && <SeloDeAssinaturaDigital />}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {requisicao.veterinarioNome}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Ícones soltos em vez dos três pontinhos: as duas
                            ações do dia a dia (abrir o PDF e compartilhar)
                            ficam a um clique, sem abrir menu. As de ciclo de
                            vida continuam aqui, só que também como ícone —
                            tirá-las da linha deixaria a requisição sem como
                            ser assinada, e sem assinatura não há o que
                            compartilhar. */}
                        <div className="flex items-center justify-end gap-0.5">
                          <BotaoDeAcao
                            icone={FileText}
                            rotulo={
                              requisicao.status === 'ASSINADO'
                                ? `Abrir PDF da requisição de ${requisicao.pacienteNome}`
                                : `Prévia em PDF da requisição de ${requisicao.pacienteNome}`
                            }
                            aoClicar={() => abrirPdf(requisicao)}
                          />

                          <BotaoDeAcao
                            icone={Share2}
                            rotulo={`Compartilhar a requisição de ${requisicao.pacienteNome}`}
                            aoClicar={() => void gerarLink(requisicao)}
                          />

                          {/* `editavel` vem do servidor: ele já pesa
                              assinatura e cancelamento. Não derivar do status. */}
                          {podeEditar && requisicao.editavel && (
                            <BotaoDeAcao
                              icone={Pencil}
                              rotulo={`Editar a requisição de ${requisicao.pacienteNome}`}
                              aoClicar={() => {
                                setEmEdicao(requisicao);
                                setFormAberto(true);
                              }}
                            />
                          )}

                          {podeAssinar && requisicao.editavel && (
                            <BotaoDeAcao
                              icone={PenLine}
                              rotulo={`Assinar a requisição de ${requisicao.pacienteNome}`}
                              aoClicar={() => setAAssinar(requisicao)}
                            />
                          )}

                          {podeExcluir && requisicao.editavel && (
                            <BotaoDeAcao
                              icone={Trash2}
                              rotulo={`Excluir a requisição de ${requisicao.pacienteNome}`}
                              aoClicar={() => setAExcluir(requisicao)}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      <RequisicaoFormDialogo
        aberto={formAberto}
        aoFechar={() => setFormAberto(false)}
        {...(emEdicao ? { requisicao: emEdicao } : {})}
      />

      <LayoutDocumentoDialogo aberto={layoutAberto} aoFechar={() => setLayoutAberto(false)} />

      {compartilhando && (
        <CompartilharDialogo
          aberto
          aoFechar={() => setCompartilhando(null)}
          link={compartilhando.link}
          tipo="REQUISICAO"
          pacienteNome={compartilhando.documento.pacienteNome}
          veterinarioNome={compartilhando.documento.veterinarioNome}
          aoEnviarPorEmail={({ email, salvarNoTutor }) =>
            compartilharPorEmail.mutateAsync({
              id: compartilhando.documento.id,
              data: { email, salvarNoTutor },
            })
          }
        />
      )}

      {dialogoDeSenha}

      <AlertDialog open={Boolean(aAssinar)} onOpenChange={(v) => !v && setAAssinar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assinar esta requisição?</AlertDialogTitle>
            <AlertDialogDescription>
              Depois de assinada ela não pode mais ser editada nem excluída, e o PDF passa a
              usar a versão congelada agora. É o que permite compartilhar o documento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aAssinar && void confirmarAssinatura(aAssinar)}>
              Assinar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(aExcluir)} onOpenChange={(v) => !v && setAExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta requisição?</AlertDialogTitle>
            <AlertDialogDescription>
              A requisição sai do histórico do paciente. Não há como desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => aExcluir && void confirmarExclusao(aExcluir)}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Um ícone clicável da coluna de ações.
 *
 * O rótulo é obrigatório e vai no `aria-label` e no `title`: sem texto visível,
 * é a única coisa que diz o que o ícone faz — para o leitor de tela e para
 * quem para o mouse em cima.
 */
function BotaoDeAcao({
  icone: Icone,
  rotulo,
  aoClicar,
}: {
  icone: LucideIcon;
  rotulo: string;
  aoClicar: () => void;
}) {
  return (
    <Button variant="ghost" size="icon" aria-label={rotulo} title={rotulo} onClick={aoClicar}>
      <Icone className="w-4 h-4" />
    </Button>
  );
}
